"use client";

// The deposit wizard's state, lifted out of the portal.
//
// `RelayWizardStateHost` is rendered by `<RelayDeposit>` ABOVE
// `ResponsiveShell` and stays mounted for the widget's whole life; the engine
// that actually owns the state (`RelayWizardEngine`, everything
// `RelayDepositBody` used to compute) is a SIBLING of the shell, mounted ONLY
// while the modal is open. That split is what makes a live dialog<->drawer
// switch non-destructive: crossing the 768px breakpoint swaps the shell's
// subtree (radix `Dialog.Root` <-> vaul `Drawer.Root`, portal content
// included), but the engine is outside it and never unmounts, so the typed
// amount, selected origin/target, the fetched deposit address and any in-flight
// tx tracking all survive.
//
// Mount-only-while-open also keeps the two properties the old in-portal
// placement gave for free:
//   * no catalog/deposit-address/quote request before the modal is opened, and
//   * a full reset on close -- the engine unmounts, so there is no state left
//     to reset (the old `WalletInfo.tsx`-style reset-on-close effect is gone).
//
// See providers/relay-wizard-provider.tsx for why the bundle travels through a
// store rather than a plain context value.
import * as React from "react";

import { trackDappUiFunnel } from "../../../lib/sentry/index";
import { resolveEnvironment } from "../../../lib/environment";
import {
  useRemoteConfigValue,
  type AppsConfig,
  resolveEnvValue,
  resolveRelayForwarderFactories,
} from "../../../lib/remote-config";
import { useDepositAddress, type UseDepositAddressOptions } from "../hooks/useDepositAddress";
import { useRelayOrders } from "../hooks/useRelayOrders";
import { useTargetTokens } from "../hooks/useTargetTokens";
import { resolveRelayApiBaseUrl } from "../lib/api-base";
import { createRelayClient } from "../lib/client";
import { countRecoverableTokens, groupForwarders, type RecoveryTargetEntry } from "../lib/recoveryGroups";
import { mergeTrustedFactories } from "../lib/verifyForwarder";
import type { OrderSummary, RecoveryInfo } from "../lib/types";
import { formatUnits, isAddress, parseUnits } from "../lib/units";
import { useRelayDepositContext } from "../providers/relay-deposit-provider";
import {
  RelayWizardStoreContext,
  createRelayWizardStore,
  type RelayWizardBundle,
  type RelayDepositView,
  type RelayWizardStore,
} from "../providers/relay-wizard-provider";
import type { RelayDepositProps, RelayWalletProps } from "../types";

const DEPOSIT_STATUS_POLL_MS = 1_000;

function propsToOpts(
  props: RelayDepositProps,
  target: string | undefined,
  wallet: RelayWalletProps,
  resolvedApiBaseUrl: string,
  trustedFactories: readonly string[] | undefined,
): UseDepositAddressOptions {
  const requireWalletConnection =
    props.requireWalletConnection ?? (props.showRecipientInput === false);
  const connectedWallet =
    wallet.walletAddress && isAddress(wallet.walletAddress)
      ? wallet.walletAddress
      : undefined;

  return {
    client: props.client,
    apiBaseUrl: resolvedApiBaseUrl,
    wallet,
    chains: props.chains,
    // In wallet-locked mode, an empty CONTROLLED recipient clears any address
    // retained by the hook after disconnect. When connected, pinning the
    // recipient to that wallet prevents a stale/host-controlled address from
    // redirecting deposits while the UI promises wallet credit.
    recipient: requireWalletConnection
      ? connectedWallet ?? ""
      : props.recipient,
    // A controlled amount can otherwise keep POST /quote alive after logout.
    amount: requireWalletConnection && !connectedWallet ? "" : props.amount,
    defaultToken: props.defaultToken,
    target,
    trustedFactories,
    onDepositAddress: props.onDepositAddress,
    onDeposited: props.onDeposited,
    onError: props.onError,
  };
}

/** Parses a human decimal amount into smallest units, or 0n if empty/invalid/zero. */
function toSmallestUnits(human: string, decimals: number): bigint {
  const trimmed = human.trim();
  if (!trimmed) return 0n;
  try {
    const value = parseUnits(trimmed, decimals);
    return value > 0n ? value : 0n;
  } catch {
    return 0n;
  }
}

function orderMatchesForwarder(
  order: OrderSummary,
  forwarderVersion: number | undefined,
  targetSymbol: string | undefined,
  targetIsDefault: boolean,
): boolean {
  if (forwarderVersion !== undefined && order.forwarderVersion !== forwarderVersion) return false;
  const orderTarget = order.target?.trim();
  if (!orderTarget) return targetIsDefault;
  return Boolean(targetSymbol && orderTarget.toLowerCase() === targetSymbol.toLowerCase());
}

function orderDepositKey(orderId: string, txHash: string, blockNumber: number): string {
  return `${orderId}:${txHash.toLowerCase()}:${blockNumber}`;
}

function isFailureStatus(status: string): boolean {
  return status === "FAILED" || status === "REFUNDED" || status === "RELAY_REFUNDED" || status === "EXPIRED";
}

export interface RelayWizardStateHostProps {
  /** The lifted modal open state, owned by `<RelayDeposit>` itself. */
  open: boolean;
  children: React.ReactNode;
}

export function RelayWizardStateHost({ open, children }: RelayWizardStateHostProps) {
  // One store per widget instance, created lazily and never replaced -- the
  // provider value is therefore stable, which is exactly why the shell below
  // never remounts when `open` flips.
  const [store] = React.useState(createRelayWizardStore);

  return (
    <RelayWizardStoreContext.Provider value={store}>
      {/* Sibling, not wrapper: `children` keeps its position (and its mounted
          instances) whether or not the engine is present. */}
      {open && <RelayWizardEngine store={store} />}
      {children}
    </RelayWizardStoreContext.Provider>
  );
}

/**
 * Every hook the wizard needs, run outside the modal shell. Renders nothing --
 * its only output is the bundle it publishes into `store` after each commit.
 *
 * Body ported verbatim from `RelayDepositBody` (pre-lift), minus the
 * reset-on-close effect (unmounting is the reset now) and plus the `opened`
 * funnel event, which moved here from `<RelayDeposit.Content>` so it fires once
 * per open instead of again on every dialog<->drawer swap.
 */
function RelayWizardEngine({ store }: { store: RelayWizardStore }) {
  const props = useRelayDepositContext();
  const { showTargetSelector = true } = props;
  const requireWalletConnection =
    props.requireWalletConnection ?? (props.showRecipientInput === false);
  const hasValidConnectedWallet = Boolean(
    props.walletAddress && isAddress(props.walletAddress),
  );

  // Lives above ResponsiveShell so shell swaps and tab changes cannot reset the
  // deposit form or an in-flight transaction.
  const [activeView, setActiveView] = React.useState<RelayDepositView>("deposit");
  const recoveryOpen = activeView === "recover";
  const setRecoveryOpen = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (next) => {
      setActiveView((current) => {
        const open = current === "recover";
        const shouldOpen = typeof next === "function" ? next(open) : next;
        return shouldOpen ? "recover" : "deposit";
      });
    },
    [],
  );

  React.useEffect(() => {
    trackDappUiFunnel("relay", "opened");
  }, []);

  // Selected delivery target, threaded into useDepositAddress's `target` option
  // below. useTargetTokens needs `catalog.targets` -- which is itself part of
  // useDepositAddress's OWN return value -- so the symbol can't be computed
  // before this render's useDepositAddress call at all; instead it's synced into
  // its own state via the effect below, one render behind useTargetTokens'
  // `selected`. That extra render is what makes BOTH the initial catalog-driven
  // default AND a later user-driven `setSelectedTarget` call reach
  // useDepositAddress's `target` option (a plain ref mutated in the render body
  // would carry the INITIAL default through fine, since `origin` itself lags
  // catalog by a render too, but would silently drop a user-driven change --
  // selecting a new target is a single state update with no further render to
  // pick up a ref written after this render's useDepositAddress call already
  // fired).
  const [targetSymbol, setTargetSymbol] = React.useState<string | undefined>(undefined);

  // Resolution order: `client` (built downstream, never needs a URL at all) >
  // `apiBaseUrl` > `environment`'s built-in default map. Computed once here
  // and threaded into every place that used to read `props.apiBaseUrl`
  // directly (propsToOpts, the recovery-badge client below, and the
  // <RecoverFunds> panel) so they all agree on the same resolved base URL.
  const resolvedApiBaseUrl = React.useMemo(
    () => resolveRelayApiBaseUrl({ apiBaseUrl: props.apiBaseUrl, environment: props.environment }),
    [props.apiBaseUrl, props.environment],
  );

  // The injected capabilities, re-bundled into the shape useDepositAddress
  // takes. Memoized on the individual functions so a host passing stable
  // references (as the hook's own doc requires) doesn't get a fresh `wallet`
  // object -- and therefore a re-fired balance/receipt effect -- every render.
  const wallet = React.useMemo<RelayWalletProps>(
    () => ({
      walletAddress: props.walletAddress,
      walletChainId: props.walletChainId,
      sendTransaction: props.sendTransaction,
      writeContract: props.writeContract,
      readContract: props.readContract,
      switchChain: props.switchChain,
      waitForReceipt: props.waitForReceipt,
      getBalance: props.getBalance,
    }),
    [
      props.walletAddress,
      props.walletChainId,
      props.sendTransaction,
      props.writeContract,
      props.readContract,
      props.switchChain,
      props.waitForReceipt,
      props.getBalance,
    ],
  );

  // CDN apps.json relay ops-knobs, read ONCE up here because the trusted
  // factory list must exist before the useDepositAddress call below (its
  // one-click verification consumes it); min/max are read further down from
  // the same object. Only role-labelled `forwarderFactories` is read by current
  // code; a legacy `trustedFactories` array is deliberately ignored here. The
  // resolved list REPLACES the pinned
  // defaults. A host's explicit prop still wins.
  const resolvedEnv = resolveEnvironment(props.environment);
  const remoteRelayConfig = useRemoteConfigValue<
    AppsConfig,
    { min?: number | string; max?: number | string; trustedFactories?: string[] }
  >("apps.json", resolvedEnv, (config) => ({
    min: resolveEnvValue(config?.relay?.minDepositAmount, resolvedEnv),
    max: resolveEnvValue(config?.relay?.maxDepositAmount, resolvedEnv),
    trustedFactories: resolveRelayForwarderFactories(config, resolvedEnv),
  }));
  const effectiveTrustedFactories = React.useMemo(
    () => props.trustedFactories ?? mergeTrustedFactories(remoteRelayConfig?.trustedFactories),
    [props.trustedFactories, remoteRelayConfig?.trustedFactories],
  );

  const {
    isConnected,
    catalog,
    catalogLoading,
    catalogError,
    origin,
    setOrigin,
    amount,
    setAmount,
    recipient,
    setRecipient,
    recipientValid,
    balance,
    balanceLoading,
    insufficientBalance,
    depositAddress,
    depositAddressResult,
    depositAddressLoading,
    depositAddressError,
    quote,
    quoteLoading,
    quoteError,
    canDeposit,
    deposit,
    depositState,
  } = useDepositAddress(propsToOpts(props, targetSymbol, wallet, resolvedApiBaseUrl, effectiveTrustedFactories));

  const {
    options: targetOptions,
    selected: selectedTarget,
    setSelected: setSelectedTarget,
  } = useTargetTokens({
    catalogTargets: catalog?.targets ?? [],
    filter: props.targetTokens,
    defaultTarget: props.defaultTarget,
  });

  React.useEffect(() => {
    setTargetSymbol(selectedTarget?.symbol);
  }, [selectedTarget?.symbol]);

  // Amount/recipient inputs are hidden on SourceForm when the host controls
  // them via props -- the host owns showing/editing those values elsewhere.
  // `showRecipientInput: false` additionally hides the recipient field even
  // when uncontrolled (wallet-locked hosts): the recipient then only ever
  // comes from the connected wallet's default.
  const showAmountInput = props.amount === undefined && !props.hideAmount;
  const showRecipientInput =
    !requireWalletConnection &&
    (props.showRecipientInput ?? true) &&
    props.recipient === undefined;

  // propsToOpts pins this value to the connected wallet in guarded mode, so
  // the same predicate safely gates address-bound orders/recovery reads too.
  const hasResolvedRecipient = recipientValid && recipient.trim().length > 0 && isAddress(recipient);

  // Neither a connected wallet nor a usable recipient (host-supplied or typed):
  // there is no identity to mint a deposit address for yet, so a connect prompt
  // is shown -- as a NOTICE ABOVE the form, never instead of it. The source
  // widget deliberately lets a DISCONNECTED user type a recipient into
  // SourceForm and get a deposit address for it (no wallet required), so hiding
  // the form here would remove a real capability. The notice disappears as soon
  // as either identity source resolves.
  const controlledRecipient =
    !requireWalletConnection &&
    props.recipient !== undefined &&
    props.recipient.trim().length > 0;
  const showConnectPrompt = requireWalletConnection
    ? !hasValidConnectedWallet || !hasResolvedRecipient
    : !isConnected && !controlledRecipient && !hasResolvedRecipient;
  // The source widget's own hint, kept for the remaining cases the connect
  // prompt doesn't speak to (e.g. a connected user who cleared/broke the
  // recipient field) so the two are never on screen together.
  const showRecipientHint = Boolean(catalog) && !hasResolvedRecipient && !showConnectPrompt;

  // Gates the recovery panel actions and the badge fan-out effect below. The
  // Recover tab itself is always rendered so the Deposit navigation remains
  // stable while wallet capabilities resolve or change. The
  // spec (§6) and useRecovery.recover()'s own guard (useRecovery.ts:220) agree
  // that recovery needs `writeContract` + `readContract` + `waitForReceipt` --
  // missing any one of the three means recover() would throw immediately.
  // `getBalance`/`sendTransaction` are irrelevant here -- recovery never reads
  // a balance or does a plain transfer.
  // Opt-in (default OFF) on top of the capability trio: recovery signs on BSC,
  // which embedded-wallet users can never do (same reasoning as one-click).
  const canRecover =
    (props.showRecovery ?? false) &&
    hasValidConnectedWallet &&
    Boolean(wallet.writeContract && wallet.readContract && wallet.waitForReceipt);

  // ---- Aggregate recoverable-token badge -----------------------------------
  // A standalone client, built the same way useDepositAddress/useRecovery each
  // build their own -- the wizard fetches GET /v1/recovery per catalog target
  // ITSELF (not via useRecovery, which only ever tracks the ONE
  // currently-selected target for the open <RecoverFunds> panel) so the gear's
  // badge can reflect stuck funds across every target, even ones the user
  // hasn't selected.
  const recoveryClient = React.useMemo(() => {
    if (props.client) return props.client;
    return createRelayClient({ baseUrl: resolvedApiBaseUrl });
  }, [props.client, resolvedApiBaseUrl]);

  // The full RecoveryInfo per target, not a count (upstream e5bff75). The
  // <RecoverFunds> panel needs the whole response (forwarder, factory,
  // isDeployed, tokens[]) to render a section and sign for EACH forwarder, and
  // this is already exactly the data being fetched -- so the panel consumes it
  // directly instead of refetching the one selected target for itself.
  const [recoveryByTarget, setRecoveryByTarget] = React.useState<Record<string, RecoveryInfo>>({});
  const [recoveryTargetsLoading, setRecoveryTargetsLoading] = React.useState(false);
  // Bumped by <RecoverFunds> after a recovery changes on-chain state, so the
  // fetch below re-runs and the panel reflects the new balances/deploy state.
  const [recoveryNonce, setRecoveryNonce] = React.useState(0);
  const refreshRecovery = React.useCallback(() => setRecoveryNonce((n) => n + 1), []);
  const depositConfirmed = depositState.isConfirmed;

  // One orders subscription is shared by the live deposit monitor and the
  // recovery panel. Keeping a second EventSource for the panel would waste the
  // API's per-IP/global SSE connection budget. The stream carries no data: the
  // hook re-fetches /orders after every invalidation and every 1s while this
  // popup is open. The selected forwarder's /recovery read has its own 1s
  // polling effect below, because a sub-minimum arrival may not create or
  // change an order and therefore may emit no SSE signal at all.
  const ordersWatchEnabled =
    hasResolvedRecipient && (Boolean(depositAddressResult) || activeView !== "deposit");
  const {
    orders: watchedOrders,
    ordersLoading: watchedOrdersLoading,
    ordersError: watchedOrdersError,
    ordersInitialized: watchedOrdersInitialized,
    ordersForRecipient,
  } = useRelayOrders({
    client: recoveryClient,
    recipient: ordersWatchEnabled ? recipient : undefined,
    enabled: ordersWatchEnabled,
    // This subscription exists only while the Deposit engine is mounted/open.
    // SSE remains the immediate signal; the short REST fallback makes success
    // observable when a deployment has no event bus or an event is dropped.
    refetchInterval: DEPOSIT_STATUS_POLL_MS,
  });
  const recoveryOrders = watchedOrders;

  const currentTarget =
    selectedTarget ??
    catalog?.targets.find((target) => target.isDefault);
  const currentTargetSymbol = currentTarget?.symbol ?? catalog?.destination.symbol;
  const currentTargetIsDefault = currentTarget?.isDefault ?? true;
  const currentForwarderVersion = depositAddressResult?.version;
  const matchingOrders = React.useMemo(
    () =>
      watchedOrders.filter((order) =>
        orderMatchesForwarder(
          order,
          currentForwarderVersion,
          currentTargetSymbol,
          currentTargetIsDefault,
        ),
      ),
    [watchedOrders, currentForwarderVersion, currentTargetSymbol, currentTargetIsDefault],
  );

  // Baseline existing orders/deposits once the first snapshot for THIS
  // recipient is accepted. A reusable forwarder can aggregate a later top-up
  // into an existing order, so detecting only unseen orderIds misses the exact
  // business case this monitor exists for; deposit txHash+block is the primary
  // identity and a new orderId is only the pre-attribution fallback.
  const depositWatchBaselineRef = React.useRef<
    | {
        key: string;
        orderIds: Set<string>;
        depositKeys: Set<string>;
        statuses: Map<string, string>;
      }
    | undefined
  >(undefined);
  const [trackedOrderId, setTrackedOrderId] = React.useState<string>();
  const [latestArrivalRaw, setLatestArrivalRaw] = React.useState<bigint>();
  const depositWatchKey = `${recipient.toLowerCase()}:${currentTargetSymbol?.toLowerCase() ?? ""}:${
    currentForwarderVersion ?? ""
  }`;

  React.useEffect(() => {
    const snapshotMatchesRecipient =
      ordersForRecipient?.toLowerCase() === recipient.toLowerCase();
    if (
      !ordersWatchEnabled ||
      !watchedOrdersInitialized ||
      !snapshotMatchesRecipient ||
      watchedOrdersError
    ) {
      if (!ordersWatchEnabled) {
        depositWatchBaselineRef.current = undefined;
        setTrackedOrderId(undefined);
        setLatestArrivalRaw(undefined);
      }
      return;
    }

    const currentOrderIds = new Set(matchingOrders.map((order) => order.orderId));
    const currentDepositKeys = new Set(
      matchingOrders.flatMap((order) =>
        (order.deposits ?? []).map((deposit) =>
          orderDepositKey(order.orderId, deposit.txHash, deposit.blockNumber),
        ),
      ),
    );
    const baseline = depositWatchBaselineRef.current;
    if (!baseline || baseline.key !== depositWatchKey) {
      depositWatchBaselineRef.current = {
        key: depositWatchKey,
        orderIds: currentOrderIds,
        depositKeys: currentDepositKeys,
        statuses: new Map(matchingOrders.map((order) => [order.orderId, order.status])),
      };
      setTrackedOrderId(undefined);
      setLatestArrivalRaw(undefined);
      return;
    }

    let candidate: OrderSummary | undefined;
    let arrived = 0n;
    let hasParsedArrival = false;

    for (const order of matchingOrders) {
      for (const deposit of order.deposits ?? []) {
        const key = orderDepositKey(order.orderId, deposit.txHash, deposit.blockNumber);
        if (baseline.depositKeys.has(key)) continue;
        candidate ??= order;
        try {
          arrived += BigInt(deposit.amount);
          hasParsedArrival = true;
        } catch {
          // The order still counts as detected; omit only the malformed amount.
        }
      }
    }

    candidate ??= matchingOrders.find((order) => !baseline.orderIds.has(order.orderId));
    // The backend can publish an order status before deposit attribution lands.
    // In that case the same orderId/deposits snapshot changes only by status;
    // comparing statuses makes both SSE refetches and the 1s REST fallback able
    // to surface funded/processing/completed without waiting for deposits[].
    candidate ??= matchingOrders.find(
      (order) => baseline.statuses.get(order.orderId) !== order.status,
    );
    // Seen identities are monotonic for this open. A temporarily lagging
    // replica may omit a row for one fetch; replacing the sets would announce
    // it as a brand-new deposit when it reappears.
    for (const orderId of currentOrderIds) baseline.orderIds.add(orderId);
    for (const depositKey of currentDepositKeys) baseline.depositKeys.add(depositKey);
    for (const order of matchingOrders) baseline.statuses.set(order.orderId, order.status);

    if (candidate) {
      setTrackedOrderId(candidate.orderId);
      if (hasParsedArrival) setLatestArrivalRaw(arrived);
    }
  }, [
    depositWatchKey,
    matchingOrders,
    ordersForRecipient,
    ordersWatchEnabled,
    recipient,
    watchedOrdersError,
    watchedOrdersInitialized,
  ]);

  React.useEffect(() => {
    const targets = catalog?.targets ?? [];
    // Nothing to aggregate without a resolved recipient, enabled recovery
    // capabilities, or any targets at all. The tab remains visible, but no
    // badge request is needed while its actions are unavailable.
    // `recoveryClient` itself is always present now
    // (client > apiBaseUrl > environment default).
    if (!canRecover || !hasResolvedRecipient || targets.length === 0) {
      setRecoveryByTarget({});
      setRecoveryTargetsLoading(false);
      return;
    }

    let cancelled = false;
    setRecoveryTargetsLoading(true);
    Promise.all(
      targets.map((t) => recoveryClient.getRecovery({ user: recipient, target: t.symbol }).catch(() => undefined)),
    ).then((results) => {
      if (cancelled) return;
      const byTarget: Record<string, RecoveryInfo> = {};
      targets.forEach((t, i) => {
        const info = results[i];
        if (info) byTarget[t.symbol] = info;
      });
      setRecoveryByTarget(byTarget);
      setRecoveryTargetsLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // `depositConfirmed` is the refresh signal (the source widget used its
    // `orders` reference, which this modal no longer fetches): a deposit landing
    // on-chain is exactly when a target's recoverable-token set may have
    // changed. It's never read below -- write-only trigger, same pattern as
    // useRecovery's own `refreshNonce`. `recoveryNonce` is the panel's own
    // post-recovery refresh, same idiom.
  }, [recoveryClient, canRecover, hasResolvedRecipient, recipient, catalog, depositConfirmed, recoveryNonce]);

  // Per-target entries in CATALOG order, which is the order the panel's
  // sections follow.
  const recoveryTargets = React.useMemo<RecoveryTargetEntry[]>(
    () =>
      (catalog?.targets ?? [])
        .map((t) => ({ symbol: t.symbol, isDefault: t.isDefault, info: recoveryByTarget[t.symbol] }))
        .filter((e): e is RecoveryTargetEntry => e.info !== undefined),
    [catalog, recoveryByTarget],
  );

  // Counted per FORWARDER, not per target. Several delivery tokens can resolve
  // to the same forwarder -- every swap-kind target does -- and that is ONE pot
  // of money, so summing the per-target counts reported it once per target. The
  // badge then read higher than the panel could ever list, which looks like the
  // panel is hiding funds. See ../lib/recoveryGroups.ts.
  const recoverableCount = React.useMemo(
    () => countRecoverableTokens(groupForwarders(recoveryTargets)),
    [recoveryTargets],
  );

  // ---- Already-deposited, still-un-swept balance ----------------------------
  // A DELIBERATELY separate fetch from the badge aggregation above, for the
  // SELECTED target only, and gated ONLY on a resolved recipient -- no
  // capability trio, no `showRecovery` opt-in. Rationale: this one feeds the
  // minimum-deposit NOTICE, which is pure read-only guidance ("your next
  // deposit only has to cover the remainder"). Every user who can be told a
  // minimum deserves to be told it accounts for what they already sent, whether
  // or not their wallet could ever sign the recovery transaction that the gear
  // offers. Failure (or an older/absent field) degrades to the plain notice.
  const [pendingSweep, setPendingSweep] = React.useState<
    { balance: bigint; decimals: number; symbol?: string } | undefined
  >(undefined);
  const [selectedRecovery, setSelectedRecovery] = React.useState<RecoveryInfo>();

  // `targetSymbol` lags `selectedTarget` by one render (see its declaration), so
  // on a multi-target catalog there is a render where the catalog is loaded but
  // the selected target hasn't reached this state yet. Waiting for it keeps the
  // fetch to exactly ONE request per (recipient, target, origin) -- no throwaway
  // request against the backend's default target. A `targets: []` catalog never
  // resolves a target at all, and `undefined` is the right query there.
  const targetSyncPending = Boolean(catalog) && (catalog?.targets.length ?? 0) > 0 && !targetSymbol;

  React.useEffect(() => {
    // No `origin` means no catalog yet, hence no decimals and no minimum to
    // fold the pending balance into -- nothing this response could be used for.
    if (
      !hasResolvedRecipient ||
      !origin ||
      !depositAddressResult ||
      targetSyncPending
    ) {
      setPendingSweep(undefined);
      setSelectedRecovery(undefined);
      return;
    }

    // A below-minimum arrival may not create or mutate an order, so the orders
    // SSE has nothing to invalidate. Poll the selected forwarder independently
    // while this engine is mounted (the engine exists only while the popup is
    // open). Three seconds stays far below the API's 5 rps per-IP limit while
    // giving the waiting balance visibly responsive feedback.
    let cancelled = false;
    let inFlight = false;
    const expectedForwarder = depositAddressResult.forwarder.toLowerCase();
    setPendingSweep(undefined);
    setSelectedRecovery(undefined);

    async function refreshSelectedRecovery() {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const info = await recoveryClient.getRecovery({
          user: recipient,
          target: targetSymbol || undefined,
        });
        if (cancelled) return;
        // The request is target-scoped, but still bind the response to the
        // exact verified forwarder currently on screen before using its
        // balance as a success criterion.
        if (
          info?.forwarder &&
          info.forwarder.toLowerCase() !== expectedForwarder
        ) {
          setPendingSweep(undefined);
          setSelectedRecovery(undefined);
          return;
        }
        setSelectedRecovery(info);
        // Only a "sweep" recommendation means "sitting below the bridge minimum,
        // waiting to be topped up" -- "execute" is enough on its own and belongs
        // to the recovery flow, "none" has nothing there at all.
        if (!info || info.suggestedAction !== "sweep") {
          setPendingSweep(undefined);
          return;
        }
        let raw: bigint;
        try {
          raw = BigInt(info.balance);
        } catch {
          setPendingSweep(undefined);
          return;
        }
        const decimals = Number(info.decimals);
        if (raw <= 0n || !Number.isInteger(decimals) || decimals < 0) {
          setPendingSweep(undefined);
          return;
        }
        // `RecoveryInfo` carries no top-level symbol -- the only honest source is
        // a `tokens[]` entry for the same address as the top-level `token`
        // (always BSC USDT). Absent (older backend), the notice says the amount
        // without a symbol rather than guessing one.
        const symbol = info.tokens?.find(
          (t) => t.address?.toLowerCase() === info.token?.toLowerCase() && t.symbol,
        )?.symbol;
        setPendingSweep({ balance: raw, decimals, symbol });
      } catch {
        // Keep the last accepted snapshot during a transient polling failure.
        // Clearing it every three seconds would make the status card flicker.
      } finally {
        inFlight = false;
      }
    }

    void refreshSelectedRecovery();
    const recoveryPollTimer = setInterval(() => {
      void refreshSelectedRecovery();
    }, DEPOSIT_STATUS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(recoveryPollTimer);
    };
    // `depositConfirmed` restarts this effect for an immediate refresh after a
    // one-click transfer, instead of waiting for the next one-second tick.
  }, [
    recoveryClient,
    hasResolvedRecipient,
    recipient,
    targetSymbol,
    targetSyncPending,
    origin,
    depositAddressResult,
    depositConfirmed,
  ]);

  const handleMax = React.useCallback(() => {
    if (!origin || !balance) return;
    setAmount(formatUnits(balance.value, origin.token.decimals));
  }, [origin, balance, setAmount]);

  // `deposit_address_shown` used to be the step-2 ENTRY anchor (fired by
  // `goToExecute`). With no step to enter, the honest anchor is the address
  // section actually appearing on screen. Fired at most ONCE per open -- the
  // address can vanish and come back (origin/target change, a refetch) and a
  // second event would inflate the funnel's own denominator; the engine
  // unmounts on close, so the ref resets exactly when the funnel does.
  const addressShownRef = React.useRef(false);
  const hasDepositAddress = Boolean(depositAddress);
  React.useEffect(() => {
    if (!hasDepositAddress || addressShownRef.current) return;
    addressShownRef.current = true;
    trackDappUiFunnel("relay", "deposit_address_shown");
  }, [hasDepositAddress]);

  // (`source_reopened` left with the Back button -- there is no step to
  // reopen.)

  // `deposit()` resolves whether or not a tx was actually submitted (it reports
  // failures through `depositState.error`), so the success signal is a NEW tx
  // hash appearing -- which is also exactly what `onDeposited` fires on.
  const txHash = depositState.txHash;
  React.useEffect(() => {
    if (txHash) trackDappUiFunnel("relay", "deposit_submitted");
  }, [txHash]);

  // An ENTERED amount outside [min, max] earns an inline warning -- deposits
  // under the minimum may never be swept, and above the ceiling there may be no
  // liquidity. It BLOCKS nothing (there is nothing left to block: the reusable
  // address is on the same screen and the user sends what they like). The
  // threshold is ops-controlled via the CDN apps.json
  // (`relay.minDepositAmount`, human units, token-agnostic; read by the
  // remoteRelayConfig hook near the top of this component) with a hardcoded
  // fallback of 6 when the CDN value is missing or unreachable.
  const minHuman = (() => {
    const n = Number(remoteRelayConfig?.min);
    return Number.isFinite(n) && n >= 0 ? String(remoteRelayConfig?.min) : "6";
  })();
  // Max is ops-set only (liquidity ceiling); no max configured = no cap.
  const maxHuman = (() => {
    const n = Number(remoteRelayConfig?.max);
    return Number.isFinite(n) && n > 0 ? String(remoteRelayConfig?.max) : undefined;
  })();
  // Effective minimum = the larger of the ops-set CDN floor and the catalog's
  // own per-origin minimum -- the backend won't sweep below the catalog value
  // regardless of what ops configure.
  const minSmallest = (() => {
    if (!origin) return 0n;
    const opsMin = toSmallestUnits(minHuman, origin.token.decimals);
    const catalogMin = BigInt(origin.minAmount);
    return opsMin > catalogMin ? opsMin : catalogMin;
  })();
  const minGuidanceHuman = origin ? formatUnits(minSmallest, origin.token.decimals) : minHuman;

  // The un-swept balance folded into the minimum: because the backend sweeps on
  // the COMBINED forwarder balance, the next deposit only has to cover the
  // REMAINDER. Undefined = nothing pending (or nothing left to cover), and the
  // notice keeps its normal lines.
  //
  // Decimals (CLAUDE.md §4): `pendingSweep.balance` is in the RECOVERY token's
  // decimals (BSC USDT, 18) while `minSmallest` is in the ORIGIN token's (e.g.
  // Base USDT, 6), so the two are rescaled to a common basis BEFORE subtracting
  // -- entirely in BigInt, never through Number. Scaling DOWN floors, which is
  // the safe direction: it understates the credit, so the suggested remainder is
  // never too small to clear the minimum.
  const pendingNotice = (() => {
    if (!origin || !pendingSweep || minSmallest <= 0n) return undefined;
    const originDec = origin.token.decimals;
    const recDec = pendingSweep.decimals;
    const pendingInOrigin =
      originDec >= recDec
        ? pendingSweep.balance * 10n ** BigInt(originDec - recDec)
        : pendingSweep.balance / 10n ** BigInt(recDec - originDec);
    // Already clears the effective minimum on its own -- that is the recovery
    // flow's ("execute") case, so leave the notice alone.
    if (pendingInOrigin >= minSmallest) return undefined;
    const remaining = minSmallest - pendingInOrigin;
    return {
      pending: formatUnits(pendingSweep.balance, recDec),
      pendingSymbol: pendingSweep.symbol,
      remaining: formatUnits(remaining, originDec),
    };
  })();

  const bridgeTokenOrigin =
    catalog?.origins.find(
      (candidate) =>
        candidate.chainId === 56 &&
        Boolean(
          selectedRecovery?.token &&
            candidate.token.address.toLowerCase() === selectedRecovery.token.toLowerCase(),
        ),
    ) ??
    catalog?.origins.find(
      (candidate) => candidate.chainId === 56 && candidate.token.symbol.toUpperCase() === "USDT",
    );
  const bridgeDecimals =
    Number.isInteger(selectedRecovery?.decimals) && (selectedRecovery?.decimals ?? -1) >= 0
      ? selectedRecovery!.decimals
      : bridgeTokenOrigin?.token.decimals;
  const bridgeSymbol =
    selectedRecovery?.tokens?.find(
      (token) =>
        token.address?.toLowerCase() === selectedRecovery.token?.toLowerCase() &&
        token.symbol,
    )?.symbol ?? bridgeTokenOrigin?.token.symbol;
  const latestArrivalAmount =
    latestArrivalRaw !== undefined && bridgeDecimals !== undefined
      ? formatUnits(latestArrivalRaw, bridgeDecimals)
      : undefined;
  const effectiveMinimumAtForwarder = (() => {
    if (!origin || bridgeDecimals === undefined) return undefined;
    const originDecimals = origin.token.decimals;
    if (bridgeDecimals >= originDecimals) {
      return minSmallest * 10n ** BigInt(bridgeDecimals - originDecimals);
    }
    const divisor = 10n ** BigInt(originDecimals - bridgeDecimals);
    // Scaling a threshold down must round UP: rounding down could report the
    // business minimum reached one smallest unit too early.
    return (minSmallest + divisor - 1n) / divisor;
  })();

  const trackedOrder = trackedOrderId
    ? matchingOrders.find((order) => order.orderId === trackedOrderId)
    : undefined;
  // If the modal opens while funds are already at the forwarder or bridging,
  // show that live state even though it correctly belongs to the initial
  // baseline. Old terminal orders are intentionally ignored; only an order
  // detected during this open may transition to COMPLETED/FAILED here.
  const activeOrder =
    trackedOrder ??
    matchingOrders.find(
      (order) =>
        order.status === "BSC_FUNDED" ||
        order.status === "BRIDGE_INITIATED" ||
        order.status === "BRIDGE_PENDING",
    );
  const activeOrderMeetsMinimum = (() => {
    if (!activeOrder || effectiveMinimumAtForwarder === undefined) return false;
    try {
      return BigInt(activeOrder.amountIn) >= effectiveMinimumAtForwarder;
    } catch {
      return false;
    }
  })();
  const recoveryMinimumReached = (() => {
    if (!selectedRecovery || effectiveMinimumAtForwarder === undefined) return false;
    try {
      return (
        BigInt(selectedRecovery.balance) > 0n &&
        BigInt(selectedRecovery.balance) >= effectiveMinimumAtForwarder
      );
    } catch {
      return false;
    }
  })();

  const arrivalStatus: RelayWizardBundle["arrivalStatus"] = (() => {
    const common = {
      amount: latestArrivalAmount,
      symbol: bridgeSymbol,
      backendStatus: activeOrder?.status,
    };
    if (!depositAddressResult || !ordersWatchEnabled || !watchedOrdersInitialized) {
      return { phase: "connecting", ...common };
    }
    if (
      ordersForRecipient?.toLowerCase() !== recipient.toLowerCase() ||
      watchedOrdersError
    ) {
      return { phase: "unavailable", ...common };
    }
    if (trackedOrder?.status === "COMPLETED") {
      return { phase: "completed", ...common, backendStatus: trackedOrder.status };
    }
    if (trackedOrder && isFailureStatus(trackedOrder.status)) {
      return { phase: "failed", ...common, backendStatus: trackedOrder.status };
    }
    if (
      activeOrder?.status === "BRIDGE_INITIATED" ||
      activeOrder?.status === "BRIDGE_PENDING"
    ) {
      return { phase: "processing", ...common };
    }
    if (
      (activeOrder?.status === "BSC_FUNDED" && activeOrderMeetsMinimum) ||
      recoveryMinimumReached
    ) {
      return { phase: "funded", ...common };
    }
    if (trackedOrder) return { phase: "detected", ...common, backendStatus: trackedOrder.status };
    return { phase: "waiting", ...common };
  })();

  // (The old "reference quote for the minimum" fetch is gone -- the notice's
  // "min → ~delivered" row was cut on review 2026-07-31, so nothing needs it.)
  const maxSmallest = origin && maxHuman ? toSmallestUnits(maxHuman, origin.token.decimals) : undefined;
  const enteredSmallest = origin && amount.trim() ? toSmallestUnits(amount, origin.token.decimals) : 0n;
  const belowMinimum = Boolean(origin && enteredSmallest > 0n && enteredSmallest < minSmallest);
  const aboveMaximum = Boolean(
    origin && maxSmallest !== undefined && enteredSmallest > 0n && enteredSmallest > maxSmallest,
  );
  const amountHint = (() => {
    if (!origin) return undefined;
    if (belowMinimum) return `Amount is below the ${minGuidanceHuman} ${origin.token.symbol} minimum.`;
    if (aboveMaximum) return `Amount is above the ${maxHuman} ${origin.token.symbol} maximum.`;
    return undefined;
  })();

  // One-click is opt-in (default OFF): CROSS embedded-wallet users can never
  // sign on the origin chains, so the default modal only offers the QR /
  // copy-address manual path. Hosts serving external wallets opt in.
  const showOneClick = props.showOneClickDeposit ?? false;

  // (The `quoteBlocks`/`canContinue` gate that lived here is gone with the
  // Continue button: a failing or still-loading quote is now just
  // <QuotePreview>'s own error/skeleton state next to a working deposit
  // address, which never waited on a quote in the first place.)
  const amountSmallestUnits =
    origin && amount.trim() ? toSmallestUnits(amount, origin.token.decimals).toString() : undefined;

  const bundle: RelayWizardBundle = {
    activeView,
    setActiveView,
    recoveryOpen,
    setRecoveryOpen,

    resolvedApiBaseUrl,
    wallet,

    catalog,
    catalogLoading,
    catalogError,
    origin,
    setOrigin,
    amount,
    setAmount,
    recipient,
    setRecipient,
    recipientValid,
    balance,
    balanceLoading,
    insufficientBalance,
    depositAddress,
    depositAddressLoading,
    depositAddressError,
    arrivalStatus,
    quote,
    quoteLoading,
    quoteError,
    canDeposit,
    deposit,
    depositState,

    targetOptions,
    selectedTarget,
    setSelectedTarget,

    showAmountInput,
    minNotice: origin
      ? {
          min: minGuidanceHuman,
          symbol: origin.token.symbol,
          ...pendingNotice,
        }
      : undefined,
    showRecipientInput,
    showTargetSelector,
    showOneClick,
    showConnectPrompt,
    showRecipientHint,
    canRecover,
    recoverableCount,
    recoveryTargetsLoading,
    recoveryTargets,
    recoveryOrders,
    historyOrdersLoading: watchedOrdersLoading,
    historyOrdersError: watchedOrdersError,
    refreshRecovery,
    trustedFactories: effectiveTrustedFactories,
    amountHint,
    amountSmallestUnits,
    onMax: showAmountInput && balance && balance.value > 0n ? handleMax : undefined,
  };

  // Published in a LAYOUT effect, so the body (whose own subscribe/read layout
  // effect runs after this one -- sibling fibers commit in tree order) is
  // re-rendered before the browser paints: no empty-modal frame on open, and no
  // blank frame after a dialog<->drawer swap either.
  React.useLayoutEffect(() => {
    store.publish(bundle);
  });

  // Closing (or unmounting the widget) drops the bundle. Separate effect with a
  // stable dep so the cleanup runs ONLY on unmount, never between publishes.
  React.useLayoutEffect(() => {
    return () => store.publish(null);
  }, [store]);

  return null;
}
